// Realtime Voice Layer — Step 1 (narrator-only, read-only).
// Design: Content post #1215, FE artifact p-83022cd0ae90.
//
// The narrator listens to the focused agent's live stream and speaks an ambient
// AgentOS-voice narration. In narrator-only mode it can do NOTHING but talk —
// the token is minted tool-less (server-enforced by AiApi), so the model cannot
// emit a function_call. Audio runs Browser↔OpenAI directly (WebRTC, lowest
// latency); the real OpenAI key never reaches the browser — AiApi mints a short-
// lived `ek_` client-secret via POST /ai/realtime/token.
const AI_API = 'https://api-ai.arkturian.com';
const OPENAI_REALTIME = 'https://api.openai.com/v1/realtime/calls';
/** Mint a short-lived ephemeral session token from AiApi (key stays server-side).
 *  `apiBase` MUST be the server-resolved `realtime_endpoint` from /auth/me
 *  (Alex→arkturian, Jascha→pdrei) — never hardcoded. The user JWT rides in the
 *  Authorization header (NEVER the body); AiApi exchanges it s2s for a grant. */
export async function mintToken(opts) {
    try {
        const token = bearerToken();
        const res = await fetch(`${opts.apiBase || AI_API}/ai/realtime/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({
                provider: 'openai',
                model: 'gpt-realtime',
                voice: opts.voice || 'marin',
                session_id: opts.sessionId,
                language: opts.language || 'de',
                companion_mode: opts.mode,
                ...(opts.companionRunId ? { companion_run_id: opts.companionRunId } : {}),
                ...(opts.voiceSessionId ? { voice_session_id: opts.voiceSessionId } : {}),
                ...(opts.detailLevel ? { detail_level: opts.detailLevel } : {}),
                confirm_api_billing: true,
            }),
        });
        if (!res.ok) {
            let msg = `token ${res.status}`;
            try {
                const j = await res.json();
                msg = j?.detail?.error || j?.error || msg;
            }
            catch { /* keep status */ }
            return { client_secret: '', model: '', error: msg };
        }
        return await res.json();
    }
    catch (e) {
        return { client_secret: '', model: '', error: e instanceof Error ? e.message : 'token network error' };
    }
}
// Redaction boundary (Codex code-review 2026-06-23): the agent's raw output
// MUST be sanitized before it leaves the browser to OpenAI — even in `balanced`.
// Strip secrets/keys/tokens and hard-cap length per feed event.
const MAX_FEED_CHARS = 2000;
const SECRET_PATTERNS = [
    /\b[A-Za-z0-9_]*(?:SECRET|TOKEN|PASSWORD|PASSWD|API[_-]?KEY|PRIVATE[_-]?KEY|ACCESS[_-]?KEY)[A-Za-z0-9_]*\s*[=:]\s*\S+/gi, // env-style KEY=secret
    /\b(?:sk|ek|pk|rk|sk-proj)-[A-Za-z0-9_-]{12,}/g, // OpenAI / provider keys
    /\bBearer\s+[A-Za-z0-9._-]{12,}/gi, // bearer tokens
    /\beyJ[A-Za-z0-9._-]{20,}/g, // JWTs
    /\b(?:AKIA|ASIA)[A-Z0-9]{12,}\b/g, // AWS access key ids
    /\bghp_[A-Za-z0-9]{20,}/g, // GitHub PATs
    /-----BEGIN [A-Z ]*PRIVATE KEY-----[\s\S]*?-----END [A-Z ]*PRIVATE KEY-----/g, // PEM blocks
];
export function sanitizeForFeed(text) {
    let out = text;
    for (const re of SECRET_PATTERNS)
        out = out.replace(re, '[redacted]');
    if (out.length > MAX_FEED_CHARS)
        out = out.slice(0, MAX_FEED_CHARS) + '\n…[gekürzt]';
    return out;
}
// Map AiApi's closed error-enum (PR #68) to user-friendly German messages.
// Unknown codes pass through unchanged (so already-friendly errors survive).
export function friendlyVoiceError(code) {
    const map = {
        realtime_not_enabled: 'Voice ist für deinen Account nicht aktiviert.',
        daily_budget_exceeded: 'Tageslimit erreicht — morgen wieder verfügbar.',
        max_parallel_exceeded: 'Du hast bereits eine aktive Voice-Session laufen.',
        auth_down: 'Service kurzfristig nicht erreichbar, bitte später versuchen.',
        realtime_grant_expired: 'Sitzung abgelaufen — bitte erneut starten.',
        realtime_grant_invalid: 'Sitzung ungültig — bitte erneut starten.',
        realtime_scope_missing: 'Diese Funktion ist nicht freigeschaltet.',
        realtime_profile_misconfigured: 'Voice-Profil für deinen Account ist nicht konfiguriert.',
        realtime_wrong_profile: 'Falscher Voice-Host für deinen Account.',
    };
    return map[code] || code;
}
/** Exchange the local SDP offer for OpenAI's answer using the ephemeral secret. */
export async function openaiSdpExchange(model, clientSecret, offerSdp) {
    const res = await fetch(`${OPENAI_REALTIME}?model=${encodeURIComponent(model)}`, {
        method: 'POST',
        body: offerSdp,
        headers: { Authorization: `Bearer ${clientSecret}`, 'Content-Type': 'application/sdp' },
    });
    if (!res.ok)
        throw new Error(`OpenAI SDP ${res.status}: ${await res.text().catch(() => '')}`);
    return res.text();
}
// Dev-only narration-log upload (Alex 2026-06-24): in developer mode the client
// auto-uploads the full session transcript (feed ↔ narration ↔ operator, with
// ts/kind/agent/epoch/seg) so CloudV2 can analyse what reached the narrator and
// how it behaved — no manual export. Owner-scoped via the user JWT, upsert by
// voice_session_id, toggleable (off by default). MUST be switched off again
// after the analysis window.
// Bearer resolution is INJECTABLE so this module is auth-agnostic and portable
// across federation hosts (Realtime-Guide work 2026-06-25, post #1233): AgentOS
// reads `ark-auth` from localStorage by default; the Wanderlaut/audio-guide app
// injects its own `getToken()` (`wanderlaut-access-token`, same auth-api issuer)
// via setBearerTokenResolver. Same-issuer JWT → AiApi's grant-exchange accepts
// either 1:1, no token-exchange. Default preserves all existing AgentOS callers.
const defaultBearerResolver = () => {
    try {
        const raw = localStorage.getItem('ark-auth');
        return raw ? (JSON.parse(raw)?.access_token || null) : null;
    }
    catch {
        return null;
    }
};
let bearerResolver = defaultBearerResolver;
/** Override how this module obtains the user JWT (e.g. Wanderlaut's getToken()).
 *  Call once at app init; pass nothing to restore the AgentOS localStorage default. */
export function setBearerTokenResolver(fn) {
    bearerResolver = fn || defaultBearerResolver;
}
function bearerToken() {
    return bearerResolver();
}
export async function postDevLog(payload, apiBase) {
    try {
        const token = bearerToken();
        const res = await fetch(`${apiBase || AI_API}/ai/realtime/devlog`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify(payload),
            keepalive: true,
        });
        return res.ok;
    }
    catch {
        return false;
    }
}
// Release the server-side parallel-session reservation on stop (AiApi PR #70) so
// a device switch works immediately instead of waiting for the orphan-reap TTL.
// `voiceSessionId` MUST be the same reservation key used at mint — i.e. the
// `session_id` we sent (AiApi keys on session_id || companion_run_id). Idempotent,
// owner-scoped server-side; best-effort + keepalive so it survives unmount.
export async function endSession(voiceSessionId, apiBase) {
    try {
        const token = bearerToken();
        const res = await fetch(`${apiBase || AI_API}/ai/realtime/session/end`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ voice_session_id: voiceSessionId }),
            keepalive: true,
        });
        return res.ok;
    }
    catch {
        return false;
    }
}
// Liveness heartbeat (AiApi PR #71) — POSTed every ~30s while a session is live
// so the server's short reap (REALTIME_REAP_SECONDS=90) frees a phantom slot fast
// instead of waiting 60 min / a clean /session/end. Same reservation key as mint.
// Returns: true = alive, false = slot is GONE (reaped/released elsewhere → FE must
// stop locally), null = transient (network/401) → do nothing.
export async function heartbeat(voiceSessionId, apiBase) {
    try {
        const token = bearerToken();
        const res = await fetch(`${apiBase || AI_API}/ai/realtime/session/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ voice_session_id: voiceSessionId }),
        });
        if (!res.ok)
            return null;
        const body = await res.json().catch(() => ({}));
        return body?.alive === false ? false : true;
    }
    catch {
        return null;
    }
}
export async function reportUsage(rec, apiBase) {
    try {
        const usageToken = bearerToken();
        const res = await fetch(`${apiBase || AI_API}/ai/realtime/usage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(usageToken ? { Authorization: `Bearer ${usageToken}` } : {}) },
            // `model` + `duration_sec` per AiApi's contract (pricing); the idempotency
            // keys (voice_session_id, usage_event_id) ride along in `rec`.
            body: JSON.stringify({ model: 'gpt-realtime', duration_sec: 0, ...rec }),
            keepalive: true,
        });
        if (!res.ok)
            return { ok: false };
        const body = await res.json().catch(() => ({}));
        return { ok: true, accepted: body?.accepted, deduped: body?.deduped };
    }
    catch {
        return { ok: false };
    }
}
//# sourceMappingURL=realtimeVoiceLib.js.map